import { Box, Text } from '@mantine/core';
import PlayerCircle from './PlayerCircle';
import PlayerDrawing from './PlayerDrawing';
import PlayerList from './PlayerList';
import useScreenAudio from '../hooks/useScreenAudio';

const FONT_FAMILY = '"Cabin Sketch", "Fredericka the Great", "Rock Salt", cursive';

const TEXT_SHADOW = `
  1px 1px 0px #2563EB,
  2px 2px 0px rgba(37, 99, 235, 0.6),
  -1px -1px 0px rgba(255, 255, 255, 0.8)
`;

export default function GameScreen({
  backgroundImage,
  backgroundVideo,
  backgroundColor,
  title,
  subtitle,
  introSound,
  backgroundSound,
  exitSound,
  countdownSound,
  countdownAt,
  timeLeft,
  featuredPlayer,
  featuredPlayerSize = 300,
  featuredDrawing,
  featuredDrawingSize = 400,
  judgeAward,
  judgeAwardSize = 150,
  players,
  playerLayout = 'none',
  playerSize = 200,
  playerAnimate = false,
  playerOpacityFn,
  playerHighlightFn,
  playerIsJudgeFn,
  playerPosition,
  children,
  contentPosition = 'center',
  style,
}) {
  useScreenAudio({ introSound, backgroundSound, exitSound, countdownSound, countdownAt, timeLeft });

  const fontFamily = FONT_FAMILY;

  const bgStyle = {};
  if (backgroundImage) {
    bgStyle.backgroundImage = `url(${backgroundImage})`;
    bgStyle.backgroundSize = 'cover';
    bgStyle.backgroundPosition = 'center';
    bgStyle.backgroundRepeat = 'no-repeat';
  }
  if (backgroundColor) {
    bgStyle.background = backgroundColor;
  }

  const showPlayers = players && players.length > 0 && playerLayout !== 'none';
  const featuredIndex = featuredPlayer && players
    ? players.findIndex(p => p.id === featuredPlayer.id)
    : 0;

  return (
    <Box
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: contentPosition === 'top' ? 'flex-start' : contentPosition === 'bottom' ? 'flex-end' : 'center',
        zIndex: 1000,
        overflow: 'hidden',
        ...bgStyle,
        ...style,
      }}
    >
      {timeLeft > 0 && (
        <Box
          style={{
            position: 'absolute',
            top: '1.5rem',
            left: '1.5rem',
            zIndex: 10,
            fontSize: '5rem',
            fontWeight: 900,
            fontFamily: FONT_FAMILY,
            color: timeLeft <= 5 ? '#EF4444' : '#3B82F6',
            textShadow: TEXT_SHADOW,
            lineHeight: 1,
          }}
        >
          {timeLeft}
        </Box>
      )}

      {backgroundVideo && (
        <video
          autoPlay
          loop
          muted
          playsInline
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
          }}
        >
          <source src={backgroundVideo} type="video/mp4" />
        </video>
      )}

      {title && (
        <Text
          style={{
            fontSize: '4rem',
            fontWeight: 700,
            color: '#3B82F6',
            fontFamily,
            textAlign: 'center',
            textShadow: TEXT_SHADOW,
            maxWidth: '80%',
            position: 'relative',
            zIndex: 1,
          }}
        >
          {title}
        </Text>
      )}

      {subtitle && (
        <Text
          style={{
            fontSize: '4rem',
            fontWeight: 700,
            color: '#3B82F6',
            fontFamily,
            textAlign: 'center',
            textShadow: TEXT_SHADOW,
            maxWidth: '80%',
            marginTop: '0.5rem',
            position: 'relative',
            zIndex: 1,
          }}
        >
          {subtitle}
        </Text>
      )}

      {featuredPlayer && (
        <Box style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1,
          display: 'flex',
          flexDirection: featuredDrawing ? 'row' : 'column',
          alignItems: 'center',
          gap: '2rem',
        }}>
          <Box style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '1rem',
          }}>
            <PlayerCircle
              player={featuredPlayer}
              index={featuredIndex >= 0 ? featuredIndex : 0}
              size={featuredPlayerSize}
              highlight
              animate
            />
          </Box>
          {featuredDrawing && (
            <Box style={{ position: 'relative' }}>
              <PlayerDrawing
                src={featuredDrawing}
                alt="Featured drawing"
                size={featuredDrawingSize}
                style={{
                  border: '4px solid #FFD700',
                  boxShadow: '0 0 30px rgba(255, 215, 0, 0.5), 0 8px 24px rgba(0,0,0,0.3)',
                }}
              />
              {judgeAward && (
                <Box style={{
                  position: 'absolute',
                  top: `-${Math.round(judgeAwardSize * 0.3)}px`,
                  right: `-${Math.round(judgeAwardSize * 0.3)}px`,
                  transform: 'rotate(12deg)',
                  zIndex: 2,
                }}>
                  <PlayerDrawing
                    src={judgeAward}
                    alt="Judge award"
                    size={judgeAwardSize}
                    style={{
                      border: '3px solid #FFD700',
                      borderRadius: '12px',
                      boxShadow: '0 4px 16px rgba(255, 215, 0, 0.6), 0 4px 12px rgba(0,0,0,0.3)',
                    }}
                  />
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}
      {showPlayers && (
        <PlayerList
          players={players}
          layout={playerLayout}
          size={playerSize}
          animate={playerAnimate}
          opacityFn={playerOpacityFn}
          highlightFn={playerHighlightFn}
          isJudgeFn={playerIsJudgeFn}
          style={{ position: 'relative', zIndex: 1, ...playerPosition }}
        />
      )}
      {children}
    </Box>
  );
}
